/**
 * eReg Header Component — the standard platform AppBar (h-12, the only
 * gradient in the system) with eReg's protocol/site-mode dropdown,
 * SiteSelector, and user menu in the right slot. Same controls, same order,
 * same behavior as before; styling per the SiteOperations design system.
 * The old logo link's function (navigate to Tool Hub) is carried by the
 * AppBar's built-in Tool Hub link and the user menu's "Back to Tool Hub".
 */

import { FileText, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useEreg } from '../../contexts/EregContext';
import { useAuth } from '../../../contexts/AuthContext';
import SiteSelector from '../../../components/SiteSelector';
import { AppBar } from '../../../ui';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { user, protocols, currentProtocol, setCurrentProtocol, siteMode, setSiteMode } = useEreg();
  const { currentSite } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [protocolMenuOpen, setProtocolMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const protocolMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (protocolMenuRef.current && !protocolMenuRef.current.contains(event.target as Node)) {
        setProtocolMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    navigate('/');
  };

  const handleProtocolSelect = (protocol: typeof currentProtocol) => {
    setCurrentProtocol(protocol);
    setSiteMode(false);
    setProtocolMenuOpen(false);
  };

  const handleSiteModeSelect = () => {
    setCurrentProtocol(null);
    setSiteMode(true);
    setProtocolMenuOpen(false);
  };

  return (
    <AppBar
      icon={<FileText className="h-4 w-4 text-white" />}
      toolName="eReg"
      onMenuClick={onMenuClick}
      right={
        <>
          {/* Site Selector */}
          <div className="site-selector-intelligence">
            <SiteSelector />
          </div>

          {/* Protocol/Site selector */}
          <div className="relative" ref={protocolMenuRef}>
            <button
              onClick={() => setProtocolMenuOpen(!protocolMenuOpen)}
              className="flex min-h-[44px] items-center gap-2 rounded-lg bg-white/15 px-2.5 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/60 sm:min-h-0"
            >
              <span className="hidden sm:inline">
                {siteMode
                  ? `Site Documents - ${currentSite?.name || 'Select Site'}`
                  : currentProtocol
                    ? currentProtocol.protocol_number
                    : 'Select Study'}
              </span>
              <span className="sm:hidden">
                {siteMode
                  ? 'Site'
                  : currentProtocol
                    ? currentProtocol.protocol_number.slice(0, 8)
                    : 'Select'}
              </span>
              <ChevronDown size={16} className={`transition-transform ${protocolMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {protocolMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 max-h-96 w-72 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                {/* Site Documents option */}
                {currentSite && (
                  <>
                    <button
                      onClick={handleSiteModeSelect}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        siteMode
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <div className="font-medium">Site Documents</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">CVs, licenses, certifications</div>
                    </button>
                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                  </>
                )}

                {/* Protocols */}
                {protocols.length > 0 ? (
                  protocols.map((protocol) => (
                    <button
                      key={protocol.id}
                      onClick={() => handleProtocolSelect(protocol)}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        !siteMode && currentProtocol?.id === protocol.id
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <div className="font-medium">{protocol.protocol_number}</div>
                      {protocol.protocol_title && (
                        <div className="truncate text-xs text-gray-500 dark:text-gray-400">{protocol.protocol_title}</div>
                      )}
                      <div className="mt-1 flex gap-2">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          protocol.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : protocol.status === 'startup'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'
                        }`}>
                          {protocol.status}
                        </span>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    No studies available
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-lg p-1.5 text-white transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 sm:min-h-0 sm:min-w-0"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
                <User size={16} />
              </div>
              <ChevronDown size={14} className={`hidden sm:block transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                </div>
                <Link
                  to="/ereg/settings"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <Settings size={16} />
                  Settings
                </Link>
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <LogOut size={16} />
                  Back to Tool Hub
                </button>
              </div>
            )}
          </div>
        </>
      }
    />
  );
}
